import type {
  AliyahType,
  BarMitzvahType,
  HaftarahMelodyType,
  HaftarahType,
  PronunciationType,
  SentenceGroupMelodyType,
  SentenceGroupType,
  TagType,
  TorahMelodyType,
} from 'types/types'
import { computed, type Ref, watch } from 'vue'
import type { VueLogger } from 'vue-logger-plugin'

import store from '@/store'
import { SentenceRecordingMetadataWithPitchedAudios } from '@/util/AudioUtil'
import { isSung } from '@/util/RecordingHelpers'
import { READ, RecordingVoiceStyles, SING, VoiceStyles, VoiceStyleSet } from '@/util/VoiceStyles'

export function useVoiceStyles(
  logger: VueLogger,
  barMitzvah: Ref<BarMitzvahType | null>,
  aliyah: Ref<AliyahType | null>,
  haftarah: Ref<HaftarahType | null>,
  sentenceGroup: Ref<SentenceGroupType | null>,
  voiceStyleSet: Ref<VoiceStyleSet>,
  existingSentenceRecordingMetadata: Ref<SentenceRecordingMetadataWithPitchedAudios | null>,
  isRecording: Ref<boolean>,
  autoSegmentDetect: Ref<boolean>,
) {
  // Computed properties
  const haftarahMelodyChoices = computed<HaftarahMelodyType[]>(() => {
    if (!haftarah.value) return []
    return [...store.getters.user.userHaftarahMelodies]
  })

  const inReadMode = computed(() => {
    return voiceStyleSet.value.voice === VoiceStyles[READ]
  })

  const inSingMode = computed(() => {
    return voiceStyleSet.value.voice !== '' && voiceStyleSet.value.voice !== VoiceStyles[READ]
  })

  const isMelodicStyleDisabled = computed(() => {
    return inReadMode.value || !barMitzvah.value
  })

  const nonHiddenUserTags = computed<TagType[]>(() => {
    return [...store.getters.user.userVisibleTags]
  })

  const phrasingDropdownDisabled = computed(() => {
    return isRecording.value || autoSegmentDetect.value || !!existingSentenceRecordingMetadata.value
  })

  const pronunciationChoices = computed<PronunciationType[]>(() => {
    return [...store.getters.user.userPronunciations]
  })

  const recordingFields = computed(() => {
    const fields: Array<{ key: string; title: string }> = [
      { key: 'actions', title: '' },
      { key: 'date', title: 'Date' },
    ]

    if (pronunciationChoices.value.length > 1) {
      fields.push({ key: 'pronunciation', title: 'Pronunciation' })
    }

    if (nonHiddenUserTags.value.length) {
      fields.push({ key: 'tags', title: 'Style' })
    }

    fields.push({ key: 'voice', title: 'Voice' })

    if (torahMelodyChoices.value.length > 1 || haftarahMelodyChoices.value.length > 1 || sentenceGroupMelodyChoices.value.length > 1) {
      fields.push({ key: 'melody', title: 'Melodic Style' })
    }

    fields.push({ key: 'warning', title: 'Status' })
    return fields
  })

  const sentenceGroupMelodyChoices = computed<SentenceGroupMelodyType[]>(() => {
    if (!sentenceGroup.value) return []
    return [...store.getters.user.userSentenceGroupMelodies]
  })

  const torahMelodyChoices = computed<TorahMelodyType[]>(() => {
    if (!aliyah.value) return []
    return [...store.getters.user.userTorahMelodies]
  })

  const voices = computed<Record<string, string>>(() => {
    const result: Record<string, string> = {}
    for (const sentenceId of store.getters.audio.recordedSentenceIds) {
      for (const metadata of store.getters.audio.getRecordingListForSentence(sentenceId)) {
        if (metadata.sentenceRecording) {
          result[metadata.sentenceRecording.id] = isSung(metadata) ? RecordingVoiceStyles[SING] : RecordingVoiceStyles[READ]
        }
      }
    }
    return result
  })

  // Auto-select the only available melody when in sing mode (matches master's setVoiceStyleMelody).
  // Clears melodies in read mode. Called on voice change and on handleEditRecording.
  const autoSelectMelody = () => {
    const vss = voiceStyleSet.value
    const syncMelody = <T>(choices: { value: T[] }, key: keyof VoiceStyleSet) => {
      if (inReadMode.value || !choices.value.length) {
        ;(vss as Record<string, unknown>)[key] = null
      } else if (choices.value.length === 1) {
        ;(vss as Record<string, unknown>)[key] = choices.value[0]
      }
    }
    syncMelody(torahMelodyChoices, 'torahMelody')
    syncMelody(haftarahMelodyChoices, 'haftarahMelody')
    syncMelody(sentenceGroupMelodyChoices, 'sentenceGroupMelody')
  }

  // When the active text type changes (e.g. aliyah → haftarah), the melody
  // choice computeds recompute. Re-sync voiceStyleSet so stale cross-type
  // melodies are cleared and the correct melody is auto-selected.
  watch([torahMelodyChoices, haftarahMelodyChoices, sentenceGroupMelodyChoices], autoSelectMelody, { immediate: true })

  return {
    autoSelectMelody,

    // Computed
    haftarahMelodyChoices,
    inReadMode,
    inSingMode,
    isMelodicStyleDisabled,
    nonHiddenUserTags,
    phrasingDropdownDisabled,
    pronunciationChoices,
    recordingFields,
    sentenceGroupMelodyChoices,
    torahMelodyChoices,
    voices,
  }
}
